import { starvedBlockCauses, deriveGraph, exitContentFor, flowDefaultAction, flowSlotId, isExitAction, parseFlowSlotId, reenterableTasks } from '@agavi/blocks';
import { DndContext, type DragEndEvent, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { OctagonAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExitContentModal } from './exit-content-modal';
import { pulseElement } from './field-reveal';
import { FlowArrivalSection } from './flow-arrival-section';
import { FlowCardTokenList } from './flow-card-token';
import { FlowChangeCard } from './flow-change-card';
import { FlowEventCard } from './flow-event-card';
import { PAUSE_MESSAGE } from './flow-paused-notice';
import FlowRuleRow from './flow-rule-row';
import { FlowSensorRung } from './flow-sensor-rung';

import type { Block, Task } from '@/types/experience';
import type { ExitContent, FlowAction, FlowChange, FlowRule, FlowSlot, TaskFlowConfig } from '@agavi/blocks';

import { AddRowButton } from '@/components/ui/add-row-button';
import { useCapability } from '@/hooks/use-capabilities';
import { exitSlotTitle } from '@/lib/flow-slot-title';
import {
  blocksChangedBy, duplicateCardIds, fallthroughCardRefs, incompleteCardRefs, nextChangeTitle, nextRuleTitle, noSensorDataBlocks, noSensorDataCardRefs,
  flowIsAuthored, getBlockLabel, getOutcomeProblem, poorSensorDataBlocks, poorSensorDataCardRefs, positiveCardRefs, shadowedCardIds, taskHasGradableBlock,
  unacceptedStarvedBlocks,
} from '@/lib/flow-utils';

interface FlowPanelProps {
  flow: TaskFlowConfig;
  blocks: Block[];
  tasks: Task[];
  /** Scopes an exit block's image and sound uploads, exactly as the task's own blocks are. */
  experienceId: string;
  currentTaskId: string;
  /**
   * A slot id (`flowSlotId`) whose exit content modal should open as soon as the panel mounts,
   * and the parent's chance to forget it.
   *
   * The board's problems panel flags blank summary feedback, which is authored nowhere on the
   * canvas: it lives in a modal hanging off one of these exits. Opening the panel is half an
   * arrival, so the trip names the slot and lands the teacher in the text itself. Consumed on
   * open, so closing the modal does not fight a prop that still says to show it.
   */
  initialOpenExit?: string | null;
  /** And which block inside that exit the trip was about, to scroll to and flash. */
  initialExitBlockId?: string;
  /**
   * A flow card (`flowCardKey`) to open the panel on — expanded, scrolled to and ringed.
   *
   * The board's problems panel reports routing faults ("the task this goes to was deleted") that
   * render nowhere but here, and here they render collapsed, in a list of a dozen. Landing on the
   * panel is not landing on the card.
   */
  initialFocusCard?: string | null;
  onInitialFocusCardShown?: () => void;
  onInitialExitOpened?: () => void;
  /** Create a new NAMED pathway task off the current task; returns its id (null on failure).
   * Threaded to every target-task picker so a teacher can spawn a named pathway while wiring flow. */
  onCreateDetourTask?: (names: { pathwayName: string; taskTitle: string }) => Promise<string | null>;
  /**
   * `label` is what Undo will say it is putting back, so it names the EDIT, not the panel.
   *
   * One `update-flow` op covers every flow edit (see `undo-ops.ts`), which makes this the only
   * thing that distinguishes "Undo: deleted event" from "Undo: moved event". `coalesce` merges a run
   * of edits to the same thing into one step — a rule title fires an onChange per keystroke.
   */
  onChange: (flow: TaskFlowConfig, label: string, coalesce?: string) => void;
}

/** A section's name. The sentence explaining how each list is read lived here and was cut: both
 * rules are legible from the lists themselves (Changes has no order to read, Navigation is a
 * ladder), and two paragraphs of standing explanation at the top of a panel are read once and
 * then are clutter forever. */
/**
 * Changes and Navigation, the panel's two sections.
 *
 * `text-xs`/`tracking-wider`, which is the app's SECTION tier — the same the feedback panel gives
 * its own headings. It was `text-[11px]`, a size used in exactly one place in the codebase: this
 * one. The app has two tiers and neither is 11px, so these sat between the section headings they
 * are and the `text-[10px]` sub-labels inside the cards below them, which is the wrong end of the
 * hierarchy to be near.
 */

function SectionHeading({ title }: { title: string }) {
  return <h3 className='text-xs font-semibold uppercase tracking-wider text-ws-muted'>{title}</h3>;
}

export default function FlowPanel({ flow, blocks, tasks, experienceId, currentTaskId, initialOpenExit, initialExitBlockId, initialFocusCard, onInitialFocusCardShown, onInitialExitOpened, onCreateDetourTask, onChange }: FlowPanelProps) {
  const { t, i18n } = useTranslation();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
  );
  // Spine position governs the correct through-line: a MID-spine task's correct is locked to "next"
  // (the mastery order can't be rewired/severed from here); only the LAST spine task or a BRANCH may
  // END the experience. Non-correct events can never End (see the built-in cards below).
  const graph = useMemo(() => deriveGraph(tasks), [tasks]);
  const spine = graph.spine;
  // Nothing follows this task, so "go" on a non-correct event has no fall-through to write: the
  // card asks for a destination instead of inventing one. The correct path is exempt, because
  // ending the experience IS its sensible forward answer.
  const noNextTask = graph.nextOf[currentTaskId] == null;
  /*
   * WHETHER A STUDENT CAN GET BACK HERE, which decides whether the controls that only differ
   * across visits appear at all. On a linear experience every one of them is inert, and an inert
   * control invites a teacher to reason about a difference that cannot occur.
   */
  const reenterable = useMemo(() => reenterableTasks(graph).has(currentTaskId), [graph, currentTaskId]);

  /*
   * EXIT CONTENT, opened from whichever card owns the slot.
   *
   * One piece of state and one modal for the whole panel rather than one per card: only one can
   * be open, and five cards each holding their own would be five places for the commit to drift.
   * The cards are handed a `{ has, onOpen }` and know nothing else about it.
   */
  const [openExit, setOpenExit] = useState<FlowSlot | null>(null);
  /** Only ever set by an arrival: a teacher who opens an exit themselves is already looking at
   *  the thing they chose, and a flash would be pointing at nothing they asked about. */
  const [exitFocusBlockId, setExitFocusBlockId] = useState<string | undefined>(undefined);
  /*
   * Opened once, by the arrival that named it. The parent's callback must be stable for that to
   * hold: an inline arrow would change every render and re-open the modal the teacher just closed.
   */
  useEffect(() => {
    if (!initialOpenExit) return;
    setOpenExit(parseFlowSlotId(initialOpenExit));
    setExitFocusBlockId(initialExitBlockId);
    onInitialExitOpened?.();
  }, [initialOpenExit, initialExitBlockId, onInitialExitOpened]);
  const exitFor = (slot: FlowSlot, action: FlowAction) => (isExitAction(action)
    ? { has: !!exitContentFor(flow, slot), onOpen: () => { setExitFocusBlockId(undefined); setOpenExit(slot); } }
    : undefined);
  const commitExit = (slot: FlowSlot, content: ExitContent | undefined) => {
    const key = flowSlotId(slot);
    const next = { ...(flow.exits ?? {}) };
    // An empty exit drops its key rather than persisting a husk that renders nothing.
    if (content) next[key] = content; else delete next[key];
    onChange(
      { ...flow, ...(Object.keys(next).length > 0 ? { exits: next } : { exits: undefined }) },
      t('task.flow.undo.setExit'),
    );
  };
  // What each built-in rung catches, named as the feedback cards it is about. Derived per render
  // from the blocks, so adding a block or reclassifying its No Answer shows up immediately.
  const incompleteCards = useMemo(() => incompleteCardRefs(blocks, t), [blocks, t]);
  const positiveCards = useMemo(() => positiveCardRefs(blocks, t), [blocks, t]);
  /**
   * All Correct is off once any Participation block has split its answer space.
   *
   * It answers "nothing asked for a decision", and a targeted state is the teacher saying the
   * answers are no longer the same — so from that point the task needs a routing table rather
   * than a default. Named rather than merely flagged, because the cause is on another screen:
   * the teacher authored feedback inside a block and the consequence surfaces here.
   */
  /**
   * No submission can leave this task: every rung that can still fire has Stay.
   *
   * All Correct is excluded when it is off, because a rung that cannot fire cannot be the exit —
   * which is exactly the combination that makes this worth warning about. Events count only if
   * one was authored; an empty list routes nothing.
   */
  const noExit = useMemo(() => {
    const stays = (a: FlowAction) => a.type === 'stay';
    const reachable: FlowAction[] = [
      flow.noAnswerAction, flow.noSensorData.action, flow.poorSensorData.action,
      ...(flow.noSensorData.rules ?? []).map((r) => r.action),
      ...(flow.poorSensorData.rules ?? []).map((r) => r.action),
      ...flow.eventRules.map((r) => r.action),
      flow.defaultAction,
      flow.allCorrectAction,
    ];
    return reachable.every(stays);
  }, [flow]);
  const noDataBlocks = useMemo(() => noSensorDataBlocks(blocks), [blocks]);
  const poorDataBlocks = useMemo(() => poorSensorDataBlocks(blocks), [blocks]);
  const noSensorDataCards = useMemo(() => noSensorDataCardRefs(blocks, t), [blocks, t]);
  const poorSensorDataCards = useMemo(() => poorSensorDataCardRefs(blocks, t), [blocks, t]);
  const fallthroughCards = useMemo(() => fallthroughCardRefs(blocks, flow.eventRules, t, i18n.language), [blocks, flow.eventRules, t, i18n.language]);
  const onSpine = spine.includes(currentTaskId);
  const correctLocked = onSpine && spine[spine.length - 1] !== currentTaskId;
  const correctAllowEnd = !correctLocked; // branch, or the last spine task → End is a valid terminus
  // A content-only "pause to read/look" task (no gradable block): nothing can be incomplete, incorrect,
  // or specially matched, so the ONLY event that applies is the correct through-line — where the task
  // goes next. Suppress the incomplete / incorrect / special events + Add Event, and strip the correct
  // card down to just its action (no match-mode, no "Blocks are…" — there are no blocks to be correct).
  // Gate on block TYPE (not seeded feedback states) so the events return the instant an input/sensor
  // block is added, not only after its first edit.
  const hasInput = taskHasGradableBlock(blocks);

  /*
   * FLOW, GATED — and the panel SHRINKS rather than disappearing.
   *
   * The button stays wherever Flow stands, because the one thing a beginner keeps here is worth
   * keeping: summary feedback, the content a student is shown on their way out, is authored from
   * this panel and from nowhere else. Taking the panel away to hide branching would take
   * "well done, now go to the next station" with it, which has nothing to do with routing.
   *
   * So with Flow off this renders the shape a content-only task already renders — one All Correct
   * card and nothing else — and that is deliberate on two counts. The teacher learns the panel in
   * its small form and finds everything in the same place when it grows; and `hasInput` had
   * already worked out what that shape should be, so this is a wider trigger for an existing
   * rendering rather than a second one to keep in step.
   *
   * The card is `locked`: the action reads as fixed text with no cycler or picker, exactly as it
   * already does mid-spine, where the meaning is the same — the through-line cannot be rewired
   * from here. A beginner's whole routing model is "finish this, go on", which is what the
   * unauthored config does anyway, so nothing about the task CHANGES. Only the controls go.
   *
   * An AUTHORED flow keeps the full panel however the capability stands. The config goes on
   * running whether or not the teacher can see it, so hiding it would leave a task routing in a
   * way nothing explains and no control can undo.
   */
  // `graph.nextOf` is the spine successor — a goto pointing there is the through-line.
  const flowAuthored = flowIsAuthored(flow, graph.nextOf[currentTaskId] ?? null);
  const mayAuthorFlow = useCapability('flow') || flowAuthored;
  /** Independent of the above: a Change is its own capability, and its own grandfather clause. */
  const mayAuthorChanges = useCapability('flowChanges') || (flow.changes?.length ?? 0) > 0;
  /** Everything below Navigation's first card: the other rungs, the events, and Add Event. */
  const showRungs = hasInput && mayAuthorFlow;

  /*
   * Blocks that can no longer reach their pass state, so All Correct can never match.
   *
   * Asked of the blocks package rather than worked out here: the reachability modules already
   * decide this exactly, and the block editors already draw an `UnreachableChip` from the same
   * computation. A second derivation would be the one a teacher believed.
   */
  /* MINUS THE ONES ALREADY ACCEPTED — see `unacceptedStarvedBlocks`. The same derivation the
     Flow button's dot reads, so the two cannot disagree about whether there is a problem. */
  const starvedBlocks = useMemo(
    () => unacceptedStarvedBlocks(blocks, flow), [blocks, flow]);
  /* What accepting would SIGN OFF: the arrangement as it stands, by block id. Computed from the
     unfiltered sweep so that accepting again after a repair-and-restarve records the new set. */
  const starvedNow = useMemo(() => starvedBlockCauses(blocks).map(b => b.id), [blocks]);
  const starvedAccepted = starvedBlocks.length === 0 && starvedNow.length > 0;

  /**
   * THE WARNING THE ALL CORRECT CARD CARRIES, named by CAUSE.
   *
   * It used to say "{blocks} has no reachable Any Answer", at the top of the panel. Both halves
   * were wrong. `any_answer` is only the pass state of a SURVEY block, so on the graded Scale
   * the owner reported it named a trigger that had nothing to do with the fault — and the fault
   * was simply that nothing in it is marked correct. And a note about one card belongs on that
   * card, not above the list.
   *
   * One message per cause, because the four have four different remedies. A mixed set falls back
   * to the plain sentence rather than picking a winner and describing the other blocks wrongly.
   */
  const sentenceFor = useCallback((starved: { id: string; cause: string }[]) => {
    if (starved.length === 0) return null;
    const names = starved
      .map(({ id }) => { const i = blocks.findIndex(b => b.id === id); return i < 0 ? null : getBlockLabel(blocks[i]!, i, t); })
      .filter((n): n is string => n !== null)
      .join(', ');
    /* WHOLE KEYS, not a built suffix: a grep for the string has to find this line, which is
       the whole reason this file counts its template-literal keys. */
    const BY_CAUSE: Record<string, string> = {
      'no-correct-option': 'task.flow.allCorrectStarvedNoCorrect',
      'fully-covered': 'task.flow.allCorrectStarvedCovered',
      'multiple-correct-single-select': 'task.flow.allCorrectStarvedMultiCorrect',
      'blank-missing-correct-word': 'task.flow.allCorrectStarvedBlankWord',
    };
    const causes = new Set(starved.map(b => b.cause));
    const key = (causes.size === 1 && BY_CAUSE[[...causes][0] ?? ''])
      || 'task.flow.allCorrectStarvedMixed';
    return t(key, { blocks: names });
  }, [blocks, t]);
  const starvedWarning = useMemo(() => sentenceFor(starvedBlocks), [sentenceFor, starvedBlocks]);
  /*
   * THE SAME SENTENCE, ONCE ACCEPTED — because the fact did not go away, only the alarm.
   *
   * A card that simply fell silent would be a rung sitting inert with nothing saying why, which
   * is the state the warning was added to end. Drawn muted by `FlowPausedNotice`.
   */
  const acceptedWarning = useMemo(
    () => (starvedAccepted ? sentenceFor(starvedBlockCauses(blocks)) : null),
    [starvedAccepted, sentenceFor, blocks]);
  const addRuleButtonRef = useRef<HTMLButtonElement>(null);
  const addChangeButtonRef = useRef<HTMLButtonElement>(null);
  const [lastMoved, setLastMoved] = useState<{ id: string; direction: 'up' | 'down' } | null>(null);
  const upButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const downButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  function addChange() {
    const change: FlowChange = {
      id: `flowchange_${crypto.randomUUID()}`,
      title: nextChangeTitle(flow.changes, t),
      matchMode: 'all',
      conditions: [],
      effects: [],
    };
    // A change is added in order to configure it, so it opens -- and the accordion closes
    // whichever was open before.
    setOpenChangeId(change.id);
    onChange({ ...flow, changes: [...flow.changes, change] }, t('task.flow.undo.addChange'));
  }

  function updateChange(id: string, patch: FlowChange) {
    // Coalesced per change, same as a rule: a run of edits to one card is one undo step.
    onChange(
      { ...flow, changes: flow.changes.map((c) => (c.id === id ? patch : c)) },
      t('task.flow.undo.editChange'),
      `change:${id}`,
    );
  }

  function deleteChange(id: string) {
    onChange({ ...flow, changes: flow.changes.filter((c) => c.id !== id) }, t('task.flow.undo.deleteChange'));
    addChangeButtonRef.current?.focus();
  }

  /**
   * The one event that opens expanded: the one just added.
   *
   * Every event used to, which meant opening Flow on a task with four of them unrolled all four and
   * left the teacher scrolling past configuration they were not looking at — and collapsing them
   * again did not stick, because the card's collapsed state is local and the panel remounts. A
   * freshly added event genuinely does want to be open; an existing one has been configured already.
   */
  /** The panel's own scroller, so an arrival can find the card it was sent to. */
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [justAddedRuleId, setJustAddedRuleId] = useState<string | null>(null);
  /** Same for a change: the one just created opens, everything else stays closed. */
  /**
   * The one change that is open, if any -- an accordion rather than per-card state.
   *
   * Every card could be open at once, and several of them are tall and near-identical, so a task
   * with a few changes scrolled as a wall with nothing marking the boundaries. Opening one now
   * closes the rest, which makes the height a non-issue instead of something to design around.
   */
  const [openChangeId, setOpenChangeId] = useState<string | null>(
    () => (initialFocusCard?.startsWith('change:') ? initialFocusCard.slice('change:'.length) : null));
  /**
   * THE CARD A PROBLEMS-PANEL TRIP WAS ABOUT.
   *
   * Held rather than acted on once, because a rule card reads it as its initial collapsed state
   * (the same device `justAddedRuleId` uses) while the scroll and the ring happen in an effect
   * below. Cleared when the teacher touches anything, so it is a landing and not a mode.
   */
  const [focusCard] = useState<string | null>(initialFocusCard ?? null);
  const focusRuleId = focusCard?.startsWith('rule:') ? focusCard.slice('rule:'.length) : null;
  useEffect(() => {
    if (!focusCard) return;
    onInitialFocusCardShown?.();
    /* A frame, so the card it names has mounted expanded before anything measures it. */
    const id = requestAnimationFrame(() => {
      const el = panelRef.current?.querySelector<HTMLElement>(`[data-flow-card="${CSS.escape(focusCard)}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      pulseElement(el);
    });
    return () => cancelAnimationFrame(id);
    // Once, for the arrival that named it: re-running would yank a teacher who has scrolled away.
  }, [focusCard]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Cards that say exactly the same thing as another in their list.
   *
   * Flagged, never paused. Two identical events BOTH fire today -- the first wins by ladder order
   * and the second is simply unreachable -- so pausing them would take away a route the lesson
   * currently takes. For Changes it is even safer: monotonic effects make a repeat idempotent.
   * Redundancy is worth saying out loud; it is not a malfunction.
   *
   * The outcome is half the fingerprint, which for Changes is the half that matters: two changes
   * opening one setting under DIFFERENT conditions is exactly what a second card is for.
   */
  const duplicateChangeIds = duplicateCardIds(
    flow.changes,
    (c) => (flow.changes.find((x) => x.id === c.id)?.effects ?? [])
      .map((e) => (e.kind === 'setFlag' ? `${e.kind}:${e.blockId}:${e.field}:${e.value}`
        : e.kind === 'setVisibility' ? `${e.kind}:${e.blockId}:${e.mode}`
        : `${e.kind}:${e.blockId}`))
      .sort().join(','),
  );
  /**
   * Navigation matches on CONDITIONS ALONE, not conditions plus action.
   *
   * The ladder takes the first match, so a second event on the same conditions is dead whatever
   * it does -- and the dangerous version is precisely the one where the actions DIFFER: two
   * events on "attempt 3", one going to another task and one staying, is a silent contradiction
   * rather than a harmless repeat. Including the action in the fingerprint missed it.
   */
  const shadowedRules = shadowedCardIds(flow.eventRules);
  const titleOf = (id: string) =>
    flow.changes.find((c) => c.id === id)?.title
    ?? flow.eventRules.find((r) => r.id === id)?.title
    ?? '';
  /**
   * The built-in events route like anything else, and could route somewhere deleted just as
   * easily -- more easily, since Default is the one a teacher is most likely to point away.
   * They were the surface the dead-target pause missed: the panel renders them directly and
   * passed no problem at all. Outcome only, because their trigger is the ladder itself.
   */
  const actionProblem = (action: FlowAction) => getOutcomeProblem({ action }, blocks, tasks);
  const actionPause = (action: FlowAction) => {
    const problem = actionProblem(action);
    if (!problem) return undefined;
    return t(PAUSE_MESSAGE[problem.kind], {
      title: (action.type === 'goto' ? action.targetTitle : undefined) ?? t('task.flow.rule.pausedTargetUnnamed'),
    });
  };

  const duplicatePartner = (ids: Set<string>, selfId: string) =>
    ids.has(selfId) ? titleOf([...ids].find((id) => id !== selfId) ?? '') || undefined : undefined;

  function addRule() {
    const newRule: FlowRule = {
      id: `flowrule_${crypto.randomUUID()}`,
      title: nextRuleTitle(flow.eventRules, t),
      matchMode: 'all',
      conditions: [],
      action: { type: 'stay' },
    };
    setJustAddedRuleId(newRule.id);
    onChange({ ...flow, eventRules: [...flow.eventRules, newRule] }, t('task.flow.undo.addEvent'));
  }

  function updateRule(id: string, patch: FlowRule) {
    // Coalesced per rule: typing a title is one step, not one per letter. Two edits to DIFFERENT
    // rules never merge, because the key carries the id.
    onChange(
      { ...flow, eventRules: flow.eventRules.map((r) => (r.id === id ? patch : r)) },
      t('task.flow.undo.editEvent'),
      `rule:${id}`,
    );
  }

  function deleteRule(id: string) {
    onChange({ ...flow, eventRules: flow.eventRules.filter((r) => r.id !== id) }, t('task.flow.undo.deleteEvent'));
    // The deleted row's own delete button unmounts with it — move focus to the one control
    // that's always present, rather than letting it drop to <body>.
    addRuleButtonRef.current?.focus();
  }

  function moveRule(id: string, direction: 'up' | 'down') {
    const from = flow.eventRules.findIndex((r) => r.id === id);
    if (from === -1) return;
    const to = direction === 'up' ? from - 1 : from + 1;
    if (to < 0 || to >= flow.eventRules.length) return;
    onChange({ ...flow, eventRules: arrayMove(flow.eventRules, from, to) }, t('task.flow.undo.moveEvent'));
    setLastMoved({ id, direction });
  }

  useEffect(() => {
    if (!lastMoved) return;
    // The button just clicked can end up disabled at its new boundary position (e.g. moving an
    // event down into the last slot disables its own "down" button) — a disabled element can't
    // take focus, so fall back to the opposite-direction button on the same row.
    const primary = lastMoved.direction === 'up' ? upButtonRefs.current.get(lastMoved.id) : downButtonRefs.current.get(lastMoved.id);
    const fallback = lastMoved.direction === 'up' ? downButtonRefs.current.get(lastMoved.id) : upButtonRefs.current.get(lastMoved.id);
    (primary && !primary.disabled ? primary : fallback)?.focus();
    setLastMoved(null);
  }, [flow.eventRules, lastMoved]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = flow.eventRules.findIndex((r) => r.id === String(active.id));
    const to = flow.eventRules.findIndex((r) => r.id === String(over.id));
    if (from !== -1 && to !== -1) onChange({ ...flow, eventRules: arrayMove(flow.eventRules, from, to) }, t('task.flow.undo.moveEvent'));
  }

  return (
    <div ref={panelRef} className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4'>
      {openExit && (
        <ExitContentModal
          content={exitContentFor(flow, openExit)}
          slotTitle={exitSlotTitle(openExit, flow, t)}
          experienceId={experienceId}
          taskId={currentTaskId}
          focusBlockId={exitFocusBlockId}
          onCommit={(content) => commitExit(openExit, content)}
          onClose={() => { setExitFocusBlockId(undefined); setOpenExit(null); }}
        />
      )}

            {/* ARRIVAL FIRST, because it happens first: entering the task, then what a submit does to
          it, then where it sends them. Absent where a student cannot get back, which is most
          tasks — see `reenterableTasks`. */}
      {reenterable && mayAuthorFlow && (
        <section className='flex flex-col gap-2'>
          <SectionHeading title={t('task.flow.arrival.sectionTitle')} />
          <FlowArrivalSection
            policy={flow.onArrival}
            blocks={blocks}
            changedBlocks={blocksChangedBy(flow)}
            onChange={(onArrival, label) => onChange({ ...flow, onArrival }, label)}
          />
        </section>
      )}

      {/* Changes above Navigation because that is the order they happen in: what this submit does
          to the task, then where it sends the student. They are independent passes — a change and a
          route can both fire on one submit — so neither heading implies the other was skipped. */}
      {hasInput && mayAuthorChanges && (
        <section className='flex flex-col gap-2'>
          <SectionHeading title={t('task.flow.changes.sectionTitle')} />
          {flow.changes.map((change) => (
            <FlowChangeCard
              key={change.id}
              collapsed={change.id !== openChangeId}
              onToggleCollapsed={() => setOpenChangeId((id) => (id === change.id ? null : change.id))}
              duplicateOf={duplicatePartner(duplicateChangeIds, change.id)}
              change={change}
              blocks={blocks}
              tasks={tasks}
              currentTaskId={currentTaskId}
              reenterable={reenterable}
              allChanges={flow.changes}
              onChange={(patch) => updateChange(change.id, patch)}
              onDelete={() => deleteChange(change.id)}
            />
          ))}
          <AddRowButton
            ref={addChangeButtonRef}
            label={t('task.flow.changes.addChange')}
            onClick={addChange}
          />
        </section>
      )}

      <SectionHeading title={t('task.flow.navigation.sectionTitle')} />

      {/* TASK level, not a card's: every rung that can still fire stays put, so no submission can
          leave. It is the one problem none of the per-card hints can state, because each card is
          individually fine — Stay is a legitimate action and the trouble is that they ALL say it. */}
      {noExit && (
        <p className='mx-4 mb-2 flex items-center gap-1.5 text-xs font-medium text-ws-warning-fg'>
          <OctagonAlert aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
          {t('task.flow.noExit')}
        </p>
      )}

      {/*
        * ALL CORRECT CANNOT FIRE, which is the other way a task holds a student for ever.
        *
        * Task level for the reason `noExit` above is: every card is individually fine. All
        * Correct's action still says "next" and the block's own panel still lists Any Answer —
        * the state simply cannot be reached, because the targeted states cover every answer and
        * `targeted` is tried first. Nothing in the ladder is wrong, so nothing in the ladder can
        * say it.
        *
        * NOT a badge on the All Correct card. That existed once — "Disabled by targeted feedback
        * in MCQ 1" — and was removed deliberately when All Correct became a POSITIVE test, on the
        * grounds that nothing is suppressed and the card should state what it requires and let
        * the blocks speak for themselves. This does not reopen that: "nothing can satisfy this
        * requirement" is a different claim from "something disabled it", and it is about the
        * TASK rather than about the card.
        *
        * The owner found it by testing — All Correct failed with no warning anywhere.
        */}
      {showRungs && (
      <FlowEventCard
        title={t('task.flow.anyIncomplete.title')}
        hint={<FlowCardTokenList lead={t('task.flow.card.anyBlockIs')} cards={incompleteCards} connector={t('task.flow.card.connectorOr')} />}
        category='gate'
        noNextTask={noNextTask}
        action={flow.noAnswerAction}
        exit={exitFor({ kind: 'noAnswer' }, flow.noAnswerAction)}
        problem={actionPause(flow.noAnswerAction)}
        paused={!!actionProblem(flow.noAnswerAction)}
        onActionChange={(action) => onChange({ ...flow, noAnswerAction: action }, t('task.flow.undo.setIncomplete'))}
        tasks={tasks}
        currentTaskId={currentTaskId}
        onCreateDetourTask={onCreateDetourTask}
      />
      )}

      {/* No data before poor data — the severity order, and it is the order they resolve in: a
          student with one dead sensor and one merely noisy one has a problem the retry rung cannot
          solve. Each hides itself when no block can report that kind of fault.

          Both go with the Flow gate. The ENGINE still takes these rungs — an empty rung matches
          whenever a sensor failure fires and holds the student with `stay`, which is the right
          answer and the one a beginner would have chosen — so hiding the cards changes which
          controls exist, never where a student ends up. */}
      {mayAuthorFlow && (<>
      <FlowSensorRung
        rung={flow.noSensorData}
        title={t('task.flow.noSensorData.title')}
        cards={noSensorDataCards}
        kind='noData'
        sensing={noDataBlocks}
        noNextTask={noNextTask}
        blocks={blocks}
        tasks={tasks}
        currentTaskId={currentTaskId}
        onCreateDetourTask={onCreateDetourTask}
        onChange={(noSensorData, label) => onChange({ ...flow, noSensorData }, label)}
      />

      <FlowSensorRung
        rung={flow.poorSensorData}
        title={t('task.flow.poorSensorData.title')}
        cards={poorSensorDataCards}
        kind='poorData'
        sensing={poorDataBlocks}
        noNextTask={noNextTask}
        blocks={blocks}
        tasks={tasks}
        currentTaskId={currentTaskId}
        onCreateDetourTask={onCreateDetourTask}
        onChange={(poorSensorData, label) => onChange({ ...flow, poorSensorData }, label)}
      />
      </>)}

      <FlowEventCard
        title={t('task.flow.allCorrect.title')}
        /* The hint answers "what makes this fire". With nothing to answer, the honest answer is
           that nothing does -- and leaving it blank made a content-only task look like a card
           that had failed to load its conditions rather than one that correctly has none. */
        /* NOTHING IS DISABLED ANY MORE. This card used to carry "Disabled by targeted feedback
           in MCQ 1", because a split survey block suppressed All Correct outright. Under the
           positive test nothing is suppressed — All Correct simply does not match when a block
           fired something other than its pass state, exactly as any other condition does not
           match. The card states what it requires and lets the blocks speak for themselves. */
        hint={hasInput
          ? <FlowCardTokenList lead={t('task.flow.card.everyBlockIs')} cards={positiveCards} connector={t('task.flow.card.connectorOr')} />
          // Same muted-italic treatment as the builder's own "no events" line
          // (`experience.builder.eventsNone`), which says the same kind of thing: this card has
          // nothing to list, and that is the finished state rather than a missing one.
          : <span className='text-[12.5px] italic text-ws-muted/70'>{t('task.flow.card.noStudentActions')}</span>}
        noNextTask={noNextTask}
        category='route'
        action={flow.allCorrectAction}
        exit={exitFor({ kind: 'allCorrect' }, flow.allCorrectAction)}
        /*
         * STARVED OUTRANKS PAUSED, because they are different sizes of wrong. A paused action
         * is about where this event SENDS a student; a starved one is about whether the event
         * can fire at all, and saying the smaller thing over the larger one would be a card
         * explaining its destination while never reaching it.
         */
        problem={starvedWarning ?? acceptedWarning ?? actionPause(flow.allCorrectAction)}
        severity={starvedWarning ? 'error' : undefined}
        /* Nothing reaches it, so there is nothing to set here — the repair is in the block that
           cannot pass. Dimmed rather than removed: the rung is still part of the ladder. And it
           stays dimmed once ACCEPTED: the rung is just as inert, and it is the alarm the teacher
           put down, not the fact. */
        ghosted={!!starvedWarning || starvedAccepted}
        /* `paused` is what makes `problem` VISIBLE on this card — it gates the notice, while
           `problem` supplies the words and `severity` the colour. So a starved card is paused
           too, or the sentence would be set and never shown. */
        paused={!!starvedWarning || starvedAccepted || !!actionProblem(flow.allCorrectAction)}
        /*
         * THE WAY TO PUT IT DOWN, and to pick it up again.
         *
         * Offered only while there IS something to accept or something accepted — never on a
         * card whose All Correct is perfectly reachable, where the button would be an invitation
         * to dismiss nothing. Writes the block ids as they stand, so a block that starves LATER
         * raises the warning again instead of inheriting the sign-off.
         */
        problemAccepted={starvedAccepted}
        onToggleProblemAccepted={starvedNow.length === 0 || !mayAuthorFlow ? undefined : () => {
          onChange(
            { ...flow, starvedAccepted: starvedAccepted ? [] : starvedNow },
            t(starvedAccepted ? 'task.flow.undo.restoreStarved' : 'task.flow.undo.acceptStarved'),
          );
        }}
        onActionChange={(action) => onChange({ ...flow, allCorrectAction: action }, t('task.flow.undo.setAllCorrect'))}
        tasks={tasks}
        currentTaskId={currentTaskId}
        onCreateDetourTask={onCreateDetourTask}
        locked={correctLocked || !mayAuthorFlow}
        allowEnd={correctAllowEnd && mayAuthorFlow}
      />

      {showRungs && (
      <div className='flex flex-col gap-2'>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          accessibility={{
            screenReaderInstructions: { draggable: t('task.flow.a11y.dragInstructions') },
            announcements: {
              onDragStart: ({ active }) => {
                const idx = flow.eventRules.findIndex((r) => r.id === String(active.id));
                return t('task.flow.a11y.dragStart', { position: idx + 1, total: flow.eventRules.length });
              },
              onDragOver: ({ over }) => {
                if (!over) return undefined;
                const idx = flow.eventRules.findIndex((r) => r.id === String(over.id));
                return t('task.flow.a11y.dragOver', { position: idx + 1, total: flow.eventRules.length });
              },
              onDragEnd: ({ over }) => {
                if (!over) return undefined;
                const idx = flow.eventRules.findIndex((r) => r.id === String(over.id));
                return t('task.flow.a11y.dragDropped', { position: idx + 1, total: flow.eventRules.length });
              },
              onDragCancel: () => t('task.flow.a11y.dragCancelled'),
            },
          }}
        >
          <SortableContext items={flow.eventRules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            <div className='flex flex-col gap-2'>
              {flow.eventRules.map((rule, i) => (
                <FlowRuleRow
                  key={rule.id}
                  rule={rule}
                  shadowedBy={shadowedRules.has(rule.id) ? titleOf(shadowedRules.get(rule.id)!) : undefined}
                  noNextTask={noNextTask}
                  index={i}
                  rulesLength={flow.eventRules.length}
                  defaultCollapsed={rule.id !== justAddedRuleId && rule.id !== focusRuleId}
                  blocks={blocks}
                  tasks={tasks}
                  currentTaskId={currentTaskId}
                  reenterable={reenterable}
                  onCreateDetourTask={onCreateDetourTask}
                  onChange={(patch) => updateRule(rule.id, patch)}
                  onDelete={() => deleteRule(rule.id)}
                  exit={exitFor({ kind: 'event', ref: rule.id }, rule.action)}
                  onMoveUp={() => moveRule(rule.id, 'up')}
                  onMoveDown={() => moveRule(rule.id, 'down')}
                  upRef={(el) => { if (el) upButtonRefs.current.set(rule.id, el); else upButtonRefs.current.delete(rule.id); }}
                  downRef={(el) => { if (el) downButtonRefs.current.set(rule.id, el); else downButtonRefs.current.delete(rule.id); }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <AddRowButton
          ref={addRuleButtonRef}
          label={t('task.flow.special.addEvent')}
          onClick={addRule}
        />
      </div>
      )}

      {/* The Default rung lists what LANDS here, joined by "and" rather than "or": an inventory of
          the feedback nothing above claimed, not a set of alternatives one of which has to hold.

          Behind the Flow gate like every other rung. Left on `hasInput` alone it reappeared the
          moment a task gained its first gradable block, which is how the owner met it: "adding
          any block reactivates flow". Unauthored it says "anything else stays", which is what
          the All Correct card already implies and what the Preview diagram draws as one row. */}
      {showRungs && (
      <FlowEventCard
        title={t('task.flow.defaultRung.title')}
        exit={exitFor({ kind: 'default' }, flowDefaultAction(flow))}
        /* Same reasoning as All Correct above: every route is caught by a rung higher up, so
           nothing falls through to here and nothing on this card can change that. */
        ghosted={fallthroughCards.length === 0 && flow.eventRules.length > 0}
        hint={fallthroughCards.length === 0 && flow.eventRules.length > 0
          ? (
            <p className='flex items-center gap-1.5 text-xs font-medium text-ws-warning-fg'>
              <OctagonAlert aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
              {t('task.flow.defaultRung.unreachable')}
            </p>
          )
          : <FlowCardTokenList lead={t('task.flow.card.catches')} cards={fallthroughCards} connector={t('task.flow.card.connectorAnd')} />}
        category='fallback'
        noNextTask={noNextTask}
        action={flow.defaultAction}
        problem={actionPause(flow.defaultAction)}
        paused={!!actionProblem(flow.defaultAction)}
        onActionChange={(action) => onChange({ ...flow, defaultAction: action }, t('task.flow.undo.setDefault'))}
        tasks={tasks}
        currentTaskId={currentTaskId}
        onCreateDetourTask={onCreateDetourTask}
      />
      )}
    </div>
  );
}
